import { useTheme } from '@mui/material/styles'
import * as turf from '@turf/turf'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type LngLat, type MapMouseEvent, Source } from 'react-map-gl/mapbox'
import { create } from 'zustand'
import { Box } from '../../components/Box.tsx'
import { Typography } from '../../components/generic.tsx'
import { IconButton } from '../../components/IconButton.tsx'
import { usePreferences } from '../../data/userProfile.ts'
import { useMap } from '../../states.ts'
import { useMapTheme } from '../../theme/mapPalette.ts'
import { OrderedLayer } from '../layers/customLayers.tsx'
import { getConversionFunction } from '../measurements/convertMeasurements.tsx'
import { MapCoordinateMarker } from './MapCoordinateMarker.tsx'

type DistanceToolState = {
	enabled: boolean
	points: LngLat[]
	setEnabled: (enabled: boolean) => void
	setPoints: (points: LngLat[]) => void
}

export const useDistanceTool = create<DistanceToolState>((set) => ({
	enabled: false,
	points: [],
	setEnabled: (enabled) => set((state) => ({ enabled, points: enabled ? state.points : [] })),
	setPoints: (points) => set({ points }),
}))

export const DistanceToolToggleButton = () => {
	const { t } = useTranslation()
	const isDistanceToolEnabled = useDistanceTool((s) => s.enabled)
	const setDistanceToolEnabled = useDistanceTool((s) => s.setEnabled)

	useEffect(() => {
		// Toggle distance tool off on unmount
		return () => setDistanceToolEnabled(false)
	}, [setDistanceToolEnabled])

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.ctrlKey && e.key.toLowerCase() === 'd') {
				setDistanceToolEnabled(!isDistanceToolEnabled)
				e.preventDefault()
			}
		}

		globalThis.addEventListener('keydown', handleKeyDown)
		return () => globalThis.removeEventListener('keydown', handleKeyDown)
	}, [isDistanceToolEnabled, setDistanceToolEnabled])

	return (
		<>
			<IconButton
				icon='Ruler'
				variant='contained'
				color='secondary'
				size='small'
				label={t(($) => $.mapTools.distanceTool.distanceTool)}
				onClick={() => setDistanceToolEnabled(!isDistanceToolEnabled)}
				sx={{ boxShadow: 2, pointerEvents: 'initial', color: isDistanceToolEnabled ? 'info.main' : undefined }}
			/>
			<DistanceToolLayer />
		</>
	)
}

const DistanceToolLayer = () => {
	const { t } = useTranslation()
	const theme = useTheme()
	const mapTheme = useMapTheme()
	const { data: prefs } = usePreferences()
	const map = useMap((s) => s.map)
	const enabled = useDistanceTool((s) => s.enabled)
	const points = useDistanceTool((s) => s.points)
	const setPoints = useDistanceTool((s) => s.setPoints)
	const [hoverLngLat, setHoverLngLat] = useState<LngLat | null>(null)
	const lastMouseLngLat = useRef<LngLat | null>(null)
	const convertDistance = getConversionFunction('DISTANCE', prefs.unitsDistance, 'KILOMETRES') || ((value: number) => value)

	const handleMouseMove = useEffectEvent((e: MapMouseEvent) => {
		lastMouseLngLat.current = e.lngLat // Always track last mouse position for the "start measuring" tooltip
		if (points.length < 2) setHoverLngLat(e.lngLat)
		else setHoverLngLat(null)
	})

	useEffect(() => {
		if (!map) return
		const handleMouseOut = () => {
			lastMouseLngLat.current = null
			setHoverLngLat(null)
		}

		map.on('mousemove', handleMouseMove)
		map.on('mouseout', handleMouseOut)
		return () => {
			map.off('mousemove', handleMouseMove)
			map.off('mouseout', handleMouseOut)
		}
	}, [map])

	useEffect(() => {
		if (!map || !enabled) return
		const handleMouseClick = (e: MapMouseEvent) => {
			if (points.length < 2) setPoints([...points, e.lngLat])
			else setPoints([e.lngLat])
		}
		map.getCanvasContainer().style.cursor = 'crosshair'
		map.on('click', handleMouseClick)
		return () => {
			map.off('click', handleMouseClick)
		}
	}, [map, enabled, points, setPoints])

	const renderPoints = hoverLngLat ? [...points, hoverLngLat] : points
	if (!enabled) return null

	return (
		<>
			{/* Tooltip before first point is set */}
			{points.length === 0 && hoverLngLat && (
				<MapCoordinateMarker latitude={hoverLngLat.lat} longitude={hoverLngLat.lng}>
					<Typography variant='body1' fontSize='small' textAlign='left' noWrap>
						{t(($) => $.mapTools.distanceTool.clickToStartMeasuringDistance)}
					</Typography>
				</MapCoordinateMarker>
			)}

			{/* Render points only after one has been placed (renderPoints includes hover so use points to determine) */}
			{points.length > 0 && (
				<Source id='distance-points' type='geojson' data={turf.points(renderPoints.map((p) => [p.lng, p.lat]))}>
					<OrderedLayer
						id='distance-points-layer'
						type='circle'
						paint={{
							'circle-radius': 6,
							'circle-color': mapTheme.distanceTool.stroke,
							'circle-stroke-width': 1,
							'circle-stroke-color': theme.palette.common.white,
						}}
					/>
				</Source>
			)}

			{/* Only render line/distance popup if at least two points are placed */}
			{renderPoints.length >= 2 && renderPoints[0] && renderPoints[1] && (
				<>
					<Source type='geojson' data={getGeodesicLine(renderPoints[0], renderPoints[1])}>
						<OrderedLayer
							id='distance-line-layer'
							type='line'
							paint={{
								'line-color': mapTheme.distanceTool.stroke,
								'line-width': 2,
								'line-dasharray': [2, 2],
							}}
						/>
					</Source>
					<MapCoordinateMarker
						latitude={renderPoints[1].lat}
						longitude={renderPoints[1].lng}
						closable
						onClose={(e) => {
							e.stopPropagation()
							setPoints([])
							if (lastMouseLngLat.current) setHoverLngLat(lastMouseLngLat.current)
						}}
					>
						<Box flex center gap={1}>
							<Typography fontSize='small' noWrap>
								{convertDistance(getDistance(renderPoints[0], renderPoints[1])).toFixed(2)}{' '}
								{t(($) => $.preferences.unitLabels[prefs.unitsDistance])}
							</Typography>
						</Box>
					</MapCoordinateMarker>
				</>
			)}
		</>
	)
}

const getDistance = (a: LngLat, b: LngLat) => turf.distance(a.toArray(), b.toArray(), { units: 'kilometers' })
const getGeodesicLine = (a: LngLat, b: LngLat) => turf.greatCircle(a.toArray(), b.toArray(), { npoints: 100 })
